import { useEffect, useState } from "react";
import { api, type GroupInfo } from "./lib/api";
import { getGroupId } from "./lib/identity";
import { ErrorScreen } from "./screens/ErrorScreen";
import { PreShow } from "./screens/PreShow";
import { Stage } from "./game/Stage";
import { PreviewApp } from "./preview/PreviewApp";

type Phase =
  | { kind: "boot" }
  | { kind: "error"; title: string; message: string }
  | { kind: "preshow"; group: GroupInfo }
  | { kind: "playing"; group: GroupInfo };

export function App(): React.ReactElement {
  const [phase, setPhase] = useState<Phase>({ kind: "boot" });
  const isPreview = window.location.pathname.replace(/\/+$/, "").endsWith("/preview");

  useEffect(() => {
    if (isPreview) return;
    const id = getGroupId();
    if (!id) {
      setPhase({
        kind: "error",
        title: "Oops, an error occurred",
        message: "This is an incorrect link — please check the link and try again.",
      });
      return;
    }

    void api.groupInfo(id).then((res) => {
      if (res.success !== 1) {
        setPhase({
          kind: "error",
          title: "Oops, an error occurred",
          message:
            "This is an invalid link, please check it and try again. If the problem persists, contact help@fastfamiliar.com.",
        });
        return;
      }
      if (res.group.number_connected > res.group.max_connections) {
        setPhase({
          kind: "error",
          title: "Oops,",
          message: `You've reached capacity — only ${res.group.max_connections} people can play at one time.`,
        });
        return;
      }
      setPhase({ kind: "preshow", group: res.group });
    });
  }, [isPreview]);

  if (isPreview) return <PreviewApp />;

  switch (phase.kind) {
    case "boot":
      return (
        <div className="center-screen">
          <p>Loading…</p>
        </div>
      );
    case "error":
      return <ErrorScreen title={phase.title} message={phase.message} />;
    case "preshow":
      return (
        <PreShow group={phase.group} onReady={() => setPhase({ kind: "playing", group: phase.group })} />
      );
    case "playing":
      return <Stage group={phase.group} />;
  }
}
